import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  Stack,
  Paper,
  Chip,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  NavigateBefore,
  NavigateNext,
  PlayArrow,
  Info,
  Refresh
} from '@mui/icons-material';
import { designTokens } from '../../theme/designTokens';
import {
  selectSelectedRecords,
  selectCurrentRecordIndex,
  selectCurrentRecord,
  selectSelectedPrompt,
  selectCurrentRecordAnalysis,
  navigateToNextRecord,
  navigateToPreviousRecord,
  setAnalysisResponse
} from '../../features/analysis/analysisSlice';

const RecordAnalysis = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const selectedRecords = useSelector(selectSelectedRecords);
  const currentRecordIndex = useSelector(selectCurrentRecordIndex);
  const currentRecord = useSelector(selectCurrentRecord);
  const selectedPrompt = useSelector(selectSelectedPrompt);
  const analysisResponse = useSelector(selectCurrentRecordAnalysis);

  const canNavigatePrevious = currentRecordIndex > 0;
  const canNavigateNext = currentRecordIndex < selectedRecords.length - 1;

  const handlePreviousRecord = () => {
    if (canNavigatePrevious) {
      dispatch(navigateToPreviousRecord());
    }
  };

  const handleNextRecord = () => {
    if (canNavigateNext) {
      dispatch(navigateToNextRecord());
    }
  };

  const handleProcessRecord = () => {
    if (currentRecord && selectedPrompt) {
      // Simulate API call with mock response
      const mockResponse = `Based on the ${selectedPrompt.name.toLowerCase()} analysis for this patient record, I've identified several key findings:

 Clinical indicators suggest elevated risk factors requiring attention
 Current medication regimen shows potential optimization opportunities
 Recommended follow-up actions have been prioritized
 No critical safety concerns identified at this time

This analysis is generated using the selected prompt: "${selectedPrompt.name}". Please review the full patient data context before making clinical decisions.`;

      dispatch(setAnalysisResponse({
        recordId: currentRecord.id,
        response: mockResponse
      }));
    }
  };

  const renderRecordData = () => {
    if (!currentRecord) return null;

    // Convert record object to displayable key-value pairs
    const recordEntries = Object.entries(currentRecord).filter(
      ([key]) => key !== 'id' // Exclude the ID field
    );

    return (
      <Paper
        elevation={0}
        sx={{
          p: designTokens.spacing.xs,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: designTokens.borderRadius.xs
        }}
      >
        <Typography variant="subtitle2" sx={{
          fontWeight: designTokens.fontWeight.semibold,
          mb: designTokens.spacing.sm,
          color: theme.palette.text.primary
        }}>
          Record Data
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 1.5 }}>
          {recordEntries.map(([key, value]) => (
            <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{
                fontWeight: designTokens.fontWeight.semibold,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {key.replace(/_/g, ' ')}
              </Typography>
              <Typography variant="body2" sx={{
                color: theme.palette.text.primary,
                wordBreak: 'break-word'
              }}>
                {value || 'N/A'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  const renderAnalysisResponse = () => {
    if (!analysisResponse) {
      return (
        <Card sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 160,
          backgroundColor: theme.palette.info.light + '10',
          border: `1px dashed ${theme.palette.info.main}`,
          borderRadius: designTokens.borderRadius.xs,
        }}>
          <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
            <Info sx={{
              fontSize: 48,
              color: theme.palette.info.main,
              mb: 1.5
            }} />
            <Typography variant="h6" sx={{
              fontWeight: designTokens.fontWeight.semibold,
              color: theme.palette.info.main,
              mb: 1
            }}>
              Process this record to view the LLM response
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`Click "Process Record" above to generate AI insights for this patient record`}
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          p: designTokens.spacing.xs,
          backgroundColor: theme.palette.success.light + '08',
          border: `1px solid ${theme.palette.success.main + '30'}`,
          borderRadius: designTokens.borderRadius.xs
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{
            fontWeight: designTokens.fontWeight.semibold,
            color: theme.palette.success.dark
          }}>
            Analysis Response
          </Typography>
          <Chip
            label="AI Generated"
            size="small"
            sx={{
              backgroundColor: theme.palette.success.main,
              color: 'white',
              fontSize: designTokens.fontSize.xs
            }}
          />
        </Stack>
        <Typography variant="body2" sx={{
          lineHeight: designTokens.lineHeight.relaxed,
          color: theme.palette.text.primary,
          whiteSpace: 'pre-wrap'
        }}>
          {analysisResponse}
        </Typography>
      </Paper>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Action Bar */}
      <Box sx={{
        p: designTokens.spacing.xs,
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        backgroundColor: theme.palette.background.paper
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {/* Navigation Controls */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tooltip title="Previous Record">
              <span>
                <IconButton
                  onClick={handlePreviousRecord}
                  disabled={!canNavigatePrevious}
                  size="small"
                >
                  <NavigateBefore />
                </IconButton>
              </span>
            </Tooltip>

            <Typography variant="body2" sx={{ mx: 2 }}>
              Record {currentRecordIndex + 1} of {selectedRecords.length}
            </Typography>

            <Tooltip title="Next Record">
              <span>
                <IconButton
                  onClick={handleNextRecord}
                  disabled={!canNavigateNext}
                  size="small"
                >
                  <NavigateNext />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {/* Process Button */}
          <Button
            variant="contained"
            startIcon={analysisResponse ? <Refresh /> : <PlayArrow />}
            onClick={handleProcessRecord}
            disabled={!selectedPrompt}
            sx={{
              textTransform: 'none',
              fontWeight: designTokens.fontWeight.semibold,
              fontSize: designTokens.fontSize.sm,
              px: designTokens.spacing.xs
            }}
          >
            {analysisResponse ? 'Reprocess Record' : 'Process Record'}
          </Button>
        </Stack>
      </Box>

      {/* Content Area */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: designTokens.spacing.xs,
        display: 'flex',
        flexDirection: 'column',
        gap: designTokens.spacing.md
      }}>
        {currentRecord ? (
          <>
            {renderRecordData()}
            <Divider />
            {renderAnalysisResponse()}
          </>
        ) : (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            flexDirection: 'column',
            gap: 1.5
          }}>
            <Typography variant="h6" color="text.secondary">
              No Record Selected
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Select records from the previous step to begin analysis
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RecordAnalysis;
